/** Classification produit — miroir heuristique backend + API pré-analyse. */

const PART_KEYWORDS =
  /écran|ecran|screen|lcd|oled|amoled|vitre|verre|glass|dalle|tactile|digitizer|batterie|battery|accu|ecouteur|écouteur|earphone|earbud|airpod|\bpods?\b|coque|housse|case|cover|chargeur|c[âa]ble|cable|adaptateur|flex\b|nappe|connecteur|bouton|cam[ée]ra|pi[eè]ce|piece|spare|remplacement|haut[\s-]?parleur|speaker|micro\b|antenne|bandeau|carte\s*m[eè]re|motherboard|écran\s+complet|ecran\s+complet|chassis|châssis|port\s+charge|sim\s+tray|vitres?\s+trempe|protecteur|film\s+protection|support\s+(t[eé]l[eé]phone|smartphone|mobile|iphone|galaxy|tablette|portable)|support\s+voiture|support\s+magn[eé]t|support\s+bureau|socle|tr[eé]pied|b[eé]quille|bequille|ring\s*holder|pop\s*socket|\bholder\b|pied\s+(de\s+)?(t[eé]l[eé]phone|smartphone)|fixation\s+(t[eé]l|smartphone|mobile)/i;

const ACCESSORY_NOT_PHONE =
  /support\s+(t[eé]l[eé]phone|smartphone|mobile|iphone|galaxy|tablette|portable)|support\s+voiture|support\s+magn[eé]t|support\s+bureau|socle|tr[eé]pied|b[eé]quille|bequille|ring\s*holder|pop\s*socket|\bholder\b|pied\s+(de\s+)?(t[eé]l[eé]phone|smartphone)|fixation\s+(t[eé]l|smartphone|mobile)|ventouse\s+(t[eé]l|smartphone)/i;

const PHONE_KEYWORDS =
  /t[eé]l[eé]phone|smartphone|tablette|mobile\s+complet|appareil\s+complet|reconditionn[eé]|occasion\s+complet|\biphone\s+\d|\biphone\s+\d{1,2}\b|\bgalaxy\s+[sza]\d|\bpixel\s+\d|\bredmi\s+\d|\bpoco\s|\brealme\s|\boneplus\s|\bmotorola\s|\bhonor\s|\bnokia\s/i;

const BRAND_ONLY =
  /\biphone\b|\bsamsung\b|\bxiaomi\b|\bhuawei\b|\boppo\b|\bgoogle\b|\boneplus\b/i;

export function suggestCategorie(categorie = '', designation = '', isPiece = false, isPhone = false) {
  if (isPhone) return 'Téléphones';
  if (!isPiece) return categorie || '';
  const low = `${categorie} ${designation}`.toLowerCase();
  if (/écran|ecran|screen|lcd|oled|dalle|tactile/.test(low)) return 'Écrans';
  if (/batterie|battery|accu/.test(low)) return 'Batteries';
  if (/ecouteur|écouteur|earphone|airpod|pod/.test(low)) return 'Accessoires';
  if (/coque|housse|case|cover|chargeur|c[âa]ble|cable/.test(low)) return 'Accessoires';
  if (/support|socle|tr[eé]pied|holder|pop\s*socket|ventouse/.test(low)) return 'Accessoires';
  return categorie || 'Pièces';
}

/** Désignation + catégorie priment — pas la référence seule. */
export function classifyArticleHeuristic(categorie = '', designation = '', reference = '', opts = {}) {
  const { is_piece: isPieceFlag, is_phone: isPhoneFlag } = opts;
  if (isPhoneFlag === true) {
    return { isPhone: true, isPiece: false, needsImei: true, ambiguous: false, confidence: 'explicit', suggestedCategorie: categorie || 'Téléphones' };
  }
  if (isPhoneFlag === false || isPieceFlag) {
    const cat = categorie || suggestCategorie(categorie, designation, true, false);
    return { isPhone: false, isPiece: true, needsImei: false, ambiguous: false, confidence: 'explicit', suggestedCategorie: cat };
  }

  let primary = `${categorie} ${designation}`.trim();
  if (!primary && reference) primary = reference;

  if (PART_KEYWORDS.test(primary)) {
    return {
      isPhone: false,
      isPiece: true,
      needsImei: false,
      ambiguous: false,
      confidence: 'heuristic',
      suggestedCategorie: categorie || suggestCategorie(categorie, designation, true, false),
    };
  }
  if (ACCESSORY_NOT_PHONE.test(primary)) {
    return {
      isPhone: false,
      isPiece: true,
      needsImei: false,
      ambiguous: false,
      confidence: 'heuristic',
      suggestedCategorie: categorie || suggestCategorie(categorie, designation, true, false) || 'Accessoires',
    };
  }
  if (PHONE_KEYWORDS.test(primary)) {
    return { isPhone: true, isPiece: false, needsImei: true, ambiguous: false, confidence: 'heuristic', suggestedCategorie: categorie || 'Téléphones' };
  }
  if (BRAND_ONLY.test(primary)) {
    return { isPhone: true, isPiece: false, needsImei: true, ambiguous: false, confidence: 'heuristic', suggestedCategorie: categorie || 'Téléphones' };
  }
  return { isPhone: false, isPiece: false, needsImei: false, ambiguous: true, confidence: 'ambiguous', suggestedCategorie: categorie || '' };
}

/** @deprecated use classifyArticleHeuristic */
export const isPhoneLikeArticle = (categorie = '', designation = '') =>
  classifyArticleHeuristic(categorie, designation).needsImei;

export function getImeiListFromItem(item) {
  const qty = Math.max(1, Number(item?.quantite ?? item?.quantite_add) || 1);
  let list = [];
  if (Array.isArray(item?.imei_list) && item.imei_list.length) list = [...item.imei_list];
  else if (Array.isArray(item?.imeis) && item.imeis.length) list = [...item.imeis];
  else if (item?.imei) list = [item.imei];
  while (list.length < qty) list.push('');
  return list.slice(0, qty);
}

export function countValidImeis(item) {
  return getImeiListFromItem(item).filter((x) => String(x || '').replace(/\D/g, '').length >= 14).length;
}

export async function analyzeStockLines(api, items, { useAi = true } = {}) {
  const res = await api.post('/stock/analyze-lines', { items, use_ai: useAi });
  return res.data;
}

export function inventoryRowToAnalyzeItem(row) {
  return {
    reference: row.reference,
    designation: row.designation,
    categorie: row.categorie || '',
    quantite_add: row.quantite_add,
    prix_vente: row.prix_vente,
    prix_achat: row.prix_achat,
    ean: row.ean,
    fournisseur: row.fournisseur,
    imei_list: getImeiListFromItem(row),
  };
}

export function formToAnalyzeItem(formData, magasinId) {
  const qty = Math.max(1, Number(formData.quantite || formData.quantite_depot || 1));
  return {
    reference: formData.reference,
    designation: formData.designation,
    categorie: formData.categorie || '',
    quantite: qty,
    prix_vente: formData.prix_vente,
    prix_achat: formData.prix_achat,
    magasin_id: magasinId,
    imei_list: formData.imei_list || (formData.deviceImei ? [formData.deviceImei] : []),
  };
}

export async function registerImeisForArticle(api, reference, imeiList, magasinId) {
  const cleaned = imeiList.map((x) => String(x || '').replace(/\D/g, '').slice(0, 20)).filter((x) => x.length >= 14);
  let ok = 0;
  for (const imei of cleaned) {
    try {
      await api.post('/stock-imei', {
        imei,
        reference,
        etat: 'neuf',
        origine: 'achat',
        note: '',
        boutique: magasinId || 'M1',
      });
      ok += 1;
    } catch {
      /* continue — caller may surface partial success */
    }
  }
  return ok;
}
